import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import Layout from '../components/Layout';
import axios from 'axios';

const Graphs = () => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const secondaryColor = theme.palette.secondary.main;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/reports/year-over-year', {
        params: { year: selectedYear },
      });
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load graph data');
      console.error('Error loading graph data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-AU').format(value);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper
          sx={{
            p: 2,
            bgcolor: 'background.paper',
            boxShadow: 3,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            {label}
          </Typography>
          {payload.map((entry, index) => {
            if (entry.dataKey === 'current_net_revenue' || entry.dataKey === 'previous_net_revenue') {
              return (
                <Typography
                  key={index}
                  variant="body2"
                  sx={{ color: entry.color }}
                >
                  {entry.name}: {formatCurrency(entry.value)}
                </Typography>
              );
            } else {
              return (
                <Typography
                  key={index}
                  variant="body2"
                  sx={{ color: entry.color }}
                >
                  {entry.name}: {formatNumber(entry.value)}
                </Typography>
              );
            }
          })}
        </Paper>
      );
    }
    return null;
  };

  // Prepare data for charts (only include months with data)
  const chartData = data?.data.filter(
    (item) =>
      item.current_net_revenue !== null ||
      item.previous_net_revenue !== null ||
      item.current_total_guests > 0 ||
      item.previous_total_guests > 0
  ) || [];

  // Generate year options (current year and previous years)
  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let year = currentYear; year >= currentYear - 5; year--) {
    yearOptions.push(year);
  }

  return (
    <Layout>
      <Container maxWidth={false} sx={{ width: '100%' }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom fontWeight={700}>
            Graphs
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Month-by-month year-over-year comparison analysis
          </Typography>

          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && data && (
          <Grid container spacing={3}>
            {/* Net Revenue Comparison Chart */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Net Revenue Comparison: {data.current_year} vs {data.previous_year}
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis
                      dataKey="month_name"
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      tick={{ fill: theme.palette.text.secondary }}
                    />
                    <YAxis
                      tickFormatter={(value) => `$${value / 1000}k`}
                      tick={{ fill: theme.palette.text.secondary }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar
                      dataKey="current_net_revenue"
                      name={`${data.current_year} Net Revenue`}
                      fill={primaryColor}
                    />
                    <Bar
                      dataKey="previous_net_revenue"
                      name={`${data.previous_year} Net Revenue`}
                      fill={secondaryColor}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* Total Guests Comparison Chart */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Total Guests Comparison: {data.current_year} vs {data.previous_year}
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis
                      dataKey="month_name"
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      tick={{ fill: theme.palette.text.secondary }}
                    />
                    <YAxis tick={{ fill: theme.palette.text.secondary }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar
                      dataKey="current_total_guests"
                      name={`${data.current_year} Total Guests`}
                      fill={primaryColor}
                    />
                    <Bar
                      dataKey="previous_total_guests"
                      name={`${data.previous_year} Total Guests`}
                      fill={secondaryColor}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>
        )}

        {!loading && !error && chartData.length === 0 && (
          <Alert severity="info">
            No data available for the selected year. Please ensure data has been synced for this period.
          </Alert>
        )}
      </Container>
    </Layout>
  );
};

export default Graphs;
